import { getState, setState, type ChatMessage, type ReactionTally } from '../store';
import {
  getChatMessages,
  sendChatMessage,
  wsUrl,
  toggleChatReaction,
  REACTION_PRESETS,
  type ReactionToggleResultData,
} from '../api/game-api';
import {
  applyReactionChangedEvent,
  computeOptimisticToggle,
  reconcileReactionToggle,
  type ReactionChangedEvent,
} from '../utils/reaction-merge';
import { ReconnectingWebSocket } from '../api/websocket';
import { navigate } from '../router';
import { showReconnecting, hideStatus } from '../components/connection-status';
import { createGameMenu } from '../components/game-menu';
import { escapeHtml } from '../utils/escape';
import { showChatNotification } from '../utils/notifications';
import { createEmojiPicker } from '../components/emoji-picker';
import { triggerAdvance } from '../utils/auto-advance';
import { getElapsedSec } from '../utils/time-utils';
import { acknowledgeChatThrough } from '../utils/chat-poll';
import { ReconcileGate } from '../utils/reconcile-gate';
import { createViewLifecycle, type ViewLifecycle } from '../utils/view-lifecycle';
import { installVisibilityReconcile } from '../utils/visibility-reconciler';
import { openPhotoSourcePicker } from '../components/photo-source-picker';

let ws: ReconnectingWebSocket | null = null;
// Plan B Task 7 (2026-05-11) — timeoutWatchdog, lightboxKeyHandler,
// chatVisibilityHandler, chatPageshowHandler are now owned by activeLc.
// The 1s timeout watchdog is registered via activeLc.setInterval; the
// lightbox Escape handler via activeLc.addListener(document, 'keydown', …);
// the visibility/pageshow listeners via the visibility-reconciler installer
// (see Step 3 of renderChatView). All four resources tear down through
// activeLc.cleanup() in destroy. chatMenuHandle / closeActiveReactionPicker
// stay module-scoped — their references mutate mid-render (game-menu rebuild,
// picker open/close) so lc.adopt would freeze the wrong reference; they're
// torn down via lc.onDestroy closures that read the latest module-level
// handle at cleanup time.
//
// Plan B Task 11 (2026-05-11) — photo-source picker extracted into
// `components/photo-source-picker.ts`; teardown lives in bodyOverlays via
// activeLc.onDestroy, no module-level slot needed.
let activeLc: ViewLifecycle | null = null;
let chatMenuHandle: { destroy: () => void } | null = null;
// Session 84.3 — module-level handle so destroyChatView() can close the
// floating reaction picker without leaking listeners. openReactionPicker()
// (defined inside renderChatView for closure access to handleToggleReaction)
// stashes its teardown fn here on open.
let closeActiveReactionPicker: (() => void) | null = null;
// /investigate 2026-04-30 iOS Safari WS-suspend — chat-view audit (gap —
// need full pattern). chat-view depends on the chat WS for live messages
// and reactions; iOS Safari may tear the socket down during a backgrounded
// suspend without firing any close event. Cold-mount `getChatMessages` only
// covers the «Safari killed and re-loaded the tab» path. The «Safari
// suspended, then resumed from frozen state» path skips render entirely.
// Background `chat-poll` short-circuits while on `#/chat` (chat-poll.ts:39),
// so without these handlers the user stares at a stale chat until they
// leave + re-enter the view. Same architectural class as the v0.79.13
// location-view fix and v0.79.12 lobby-view fix. Cleaned up in
// destroyChatView.
//
// Plan B Task 7 (2026-05-11) — listener pair migrated into activeLc via
// installVisibilityReconcile. The pre-extraction `chatVisibilityHandler` /
// `chatPageshowHandler` module-level lets are gone; the installer wires
// both events off the same composite callback (reconcile + Bug 2
// forceReconnect on the chat ws).
// Reconcile gate — owns concurrent-collapse lock + 15s abort timeout, identical
// to lobby-view's pattern. iOS Safari fires visibilitychange + pageshow within
// milliseconds on resume; without the lock the chat would render the same
// payload twice. The WS REACTION_CHANGED handler reads `gate.isInflight` to
// flag concurrent reaction edits so the in-flight reconcile prefers local
// reactions over its older server snapshot. See `chatReactionInflightUpdates`
// + `chatReactionPendingToggles` below for the merge logic.
const chatGate = new ReconcileGate();
// /review 2026-04-30 codex finding (Medium) — reaction race during inflight
// reconcile. Server's `getChatMessages` snapshot is taken at fetch start;
// reactions added between fetch start and resolve from the WS REACTION_CHANGED
// path (teammate reactions, server echo of our own) need to survive the
// merge — without this set, the merge would prefer the older server snapshot
// for shared ids and revert the live WS push. Cleared at every reconcile
// fetch start; populated only by the `ws.on('REACTION_CHANGED')` handler
// while `chatGate.isInflight` is true. Exported so tests can simulate a
// mid-fetch WS push without driving the real ws.on handler (which is mocked
// in jsdom).
export const chatReactionInflightUpdates = new Set<string>();
// /review 2026-04-30 codex follow-up findings — local toggle path needs its
// OWN tracker with a different lifecycle from the WS set above.
//
// Three findings closed by this tracker:
//   - Medium: failed local toggle reverted state to prevMessages but left
//     the messageId flagged in `chatReactionInflightUpdates`. A subsequent
//     resume fetch returning a teammate's newer tally for the same id would
//     be silently suppressed by «local wins» until the next reconcile. With
//     paired add-and-delete around the POST, a failed toggle removes the
//     marker so the next merge takes server state for that id.
//   - Low: tap → reconcile-start → reconcile-resolves-before-POST. The
//     optimistic mutation happened before any reconcile was in flight, so
//     the gate-and-add at the optimistic checkpoint never fired. The next
//     reconcile cleared the set at fetch start, then applied the older
//     server snapshot (no toggle) before the POST returned, producing a
//     visible revert/flicker even though the POST eventually self-healed.
//     This tracker is NEVER cleared at reconcile-start — it tracks the
//     lifetime of a specific local POST roundtrip, which can span any
//     number of reconciles.
//   - Medium (codex round 4): two concurrent toggles on the same messageId.
//     A plain `Set<string>` cleared the marker the moment the FIRST POST
//     settled even though the SECOND was still in flight; a resume reconcile
//     racing in between would then merge with `has(id) === false` and
//     overwrite the second toggle's optimistic state with an older server
//     snapshot. Tracker is now refcounted: `add(id)` increments per call,
//     `delete(id)` only removes the id when the last in-flight POST settles.
//     `has(id)` returns true while ANY toggle on that id is still in flight,
//     covering arbitrary overlap.
//
// Add at the optimistic checkpoint inside `handleToggleReaction` (always —
// not gated on `chatGate.isInflight`). Delete on both POST success AND
// POST failure so the tracker never accumulates beyond in-flight POSTs
// (typically 0–3). Reconcile merge consults the UNION of this tracker and
// `chatReactionInflightUpdates`. Exposes a Set-shaped API
// (add/delete/has/clear) so the source-level contract tests and existing
// call sites stay untouched; the refcount is the implementation detail.
const chatReactionPendingTogglesCounts = new Map<string, number>();
export const chatReactionPendingToggles = {
  add(id: string): void {
    chatReactionPendingTogglesCounts.set(
      id,
      (chatReactionPendingTogglesCounts.get(id) ?? 0) + 1,
    );
  },
  delete(id: string): boolean {
    const cur = chatReactionPendingTogglesCounts.get(id) ?? 0;
    if (cur <= 1) return chatReactionPendingTogglesCounts.delete(id);
    chatReactionPendingTogglesCounts.set(id, cur - 1);
    return true;
  },
  has(id: string): boolean {
    return chatReactionPendingTogglesCounts.has(id);
  },
  clear(): void {
    chatReactionPendingTogglesCounts.clear();
  },
};

// /review 2026-04-30 codex round 5 (Medium) — overlapping POSTs on the
// same messageId leak stale resolves through. Refcounting the pending-
// toggle marker is necessary but not sufficient: the success and failure
// handlers also blindly applied state derived from a snapshot that was
// already superseded by a newer concurrent toggle. Concrete repro: user
// double-taps emoji X on m1. T1 captures S0 (X absent) and POSTs an add;
// T2 captures S1 (X present, mine) and POSTs a remove. T1 returns first
// with reactions=[{X,1,mine:true}] from the server's post-T1-only view.
// `reconcileReactionToggle(getState(), T1.result)` replaces the current
// state's reactions for m1 with that older snapshot, flipping the live
// state back from S2 (X removed) to S1 (X re-added). Until T2 returns,
// the resume reconcile keeps `local wins` over server (refcount > 0),
// so the user stays in the wrong state.
//
// Fix: per-messageId generation counter. Each toggle bumps the counter
// at issue time and captures its own gen in a closure local. On resolve
// (success OR failure), the handler checks `gen.get(id) === myGen`. If
// not, a newer toggle was issued after this one and owns reconciling
// state — this stale resolve just decrements the pending marker and
// returns without touching `state.chatMessages`. Only the newest issued
// toggle's resolve mutates state, so flicker collapses to a single
// authoritative paint when the LAST POST settles.
//
// Lives in module scope (alongside `chatReactionPendingTogglesCounts`)
// so it survives view re-mounts. Exported for test reset between cases.
// Memory bound: keys are messageIds the user has tapped reactions on
// within the session; entries are not cleaned on resolve (clean-up
// would race with concurrent issues), but the unbounded growth is
// limited by the chat history the user actually interacts with.
export const chatReactionToggleGeneration = new Map<string, number>();

// /review 2026-04-30 codex round 7 (Medium) — overlapping POSTs that BOTH
// fail leave the UI in an "added" state even though the server never
// committed either toggle. Concrete repro from codex: T1 optimistic add
// (gen=1) → T2 optimistic remove (gen=2, cancels T1 to original-shape
// state) → T1 fails (gen-stale, skipped) → T2 fails (gen match, inverts
// optimistic on current). Net: state ends at T1-optimistic = "X added,
// mine=true", but server has nothing committed because both POSTs failed.
// Until the next reconcile, the user sees a reaction they never actually
// committed.
//
// Why prior fixes didn't catch it:
//   - Round 4 (refcount) — covered "concurrent reconcile sees stale local"
//     but not the all-fail rollback semantics.
//   - Round 5 (gen token) — discriminated SUCCESS resolves correctly but
//     used the same "latest owns mutation" rule for FAILURE, which is
//     wrong when two failures need to compose into a full revert.
//   - Round 6 (current-state invert) — switched failure rollback away
//     from a whole-array snapshot, but the inverse-of-optimistic only
//     undoes ONE toggle's mutation. Two cancellation-pair optimistics
//     stack on each other; inverting the LATEST leaves the FIRST in
//     effect.
//
// Fix: per-message batch tracker. The batch records:
//   - `origin` — reactions snapshot for THIS message taken at the FIRST
//     toggle in the batch (before any optimistic mutation). Used to
//     restore reactions when every toggle in the batch failed.
//   - `hasSuccess` — true if any toggle in the batch resolved
//     successfully (regardless of whether it was the latest gen). When
//     true, the batch's accumulated state has been (or will be) anchored
//     by a server tally, so a tail failure should NOT restore origin.
//   - `pendingCount` — # of toggles still in flight in this batch.
//
// Failure path uses these to choose between three actions:
//   1. `pendingCount === 0 && !hasSuccess` (last to settle, all failed)
//      → restore origin reactions for the target message. This is the
//      ONLY path that fires for the codex-round-7 repro — it walks
//      cumulative optimistic state back to pre-batch.
//   2. Stale gen → skip (latest owns mutation, OR success already
//      anchored state via reconcile).
//   3. Latest gen, mid-batch OR success-was-seen → invert optimistic on
//      current state (round-6 behavior preserved for cases where it's
//      correct: T1-success-T2-fail and the "still siblings pending"
//      branches).
//
// Origin snapshot only mutates the TARGET message's reactions, not the
// whole array — a CHAT_MESSAGE arriving mid-batch survives (round-6
// invariant preserved).
//
// Trade-off: a teammate's WS REACTION_CHANGED for THIS message landing
// during the batch is captured in state but NOT in `origin`, so an
// all-fail revert briefly wipes that teammate's reaction. The next
// reconcile (visibilitychange / mount) self-heals from server. Decided
// not worth the complexity of mid-batch origin updates.
//
// Lifetime: created on first toggle, deleted when `pendingCount === 0`
// (regardless of success/failure outcomes). New tap after drain
// re-creates a fresh batch.
//
// Exported for test reset between cases (production never resets it
// outside the natural drain).
interface ReactionBatch {
  origin: ReactionTally[];
  hasSuccess: boolean;
  pendingCount: number;
}
export const chatReactionBatches = new Map<string, ReactionBatch>();

/**
 * /review 2026-04-27 v0.79.16 (F3) symmetric — same-hash guard. The visibility/
 * pageshow handlers re-fetch the full chat history on every iOS Safari resume.
 * Without a fingerprint compare, every flip would call `setState + renderMessages`
 * even when the server response is identical, replacing `#chat-messages` DOM
 * tree, snapping scroll position to the bottom, and re-attaching reaction-pill
 * click listeners while the user's finger is still mid-tap. Hash is cheap:
 * id + per-message reaction tally for every message in the bounded history
 * (~typically <100 rows; reactions bounded to 5 presets per message).
 *
 * Exported for test access — pure function, no side effects.
 */
export function chatMessagesHash(messages: ChatMessage[]): string {
  if (messages.length === 0) return 'empty';
  return messages.map(m => {
    const r = (m.reactions ?? [])
      .map(rt => `${rt.emoji}${rt.count}${rt.mine ? '1' : '0'}`)
      .join(',');
    return `${m.id}:${r}`;
  }).join('|');
}

/**
 * /investigate 2026-04-30 iOS Safari WS-suspend — re-fetch chat history from
 * the server when the tab resumes from a suspended state. Mirrors
 * `reconcileLobbyAgainstServer` (lobby-view) and `reconcileFromServer`
 * (location-view). Four guards:
 *   1. Inflight lock — collapses concurrent visibility + pageshow firings
 *      into a single fetch.
 *   2. Route guard — bails if the user navigated away from /chat during
 *      the fetch window. Without it, a stale resolver would stomp the
 *      next view's DOM via `applyMessages`.
 *   3. WS-merge — chat history is APPEND-ONLY for ids and the server
 *      snapshot lags the live WS stream by the fetch window. Two races:
 *      (a) `CHAT_MESSAGE` push between fetch start and resolve — the new
 *      message is in `state.chatMessages` but missing from the server
 *      response. Replacing state with the snapshot would drop it. After a
 *      leave-and-reenter cycle (mount A's reconcile in flight, user
 *      navigates /chat → /riddle → /chat, mount B comes up, mount B's WS
 *      pushes m4 into state, mount A's stale resolve still holds its own
 *      `applyMessages` closure and the route guard passes because hash is
 *      back on `#/chat`), the stale resolver would erase m4.
 *      (b) Reaction mutation on an existing id between fetch start and
 *      resolve — TWO sources, tracked separately because their lifecycles
 *      differ: (i) WS `REACTION_CHANGED` (teammate or our own server echo)
 *      patches `state.chatMessages[k].reactions` with the live tally —
 *      tracked in `chatReactionInflightUpdates`, cleared at every
 *      reconcile fetch start. (ii) local `handleToggleReaction` (user
 *      tapped a pill, possibly before this reconcile started) is mid-POST
 *      to the server — tracked in `chatReactionPendingToggles`, paired
 *      add/delete around the HTTP roundtrip so it survives any reconcile
 *      that fires within that window. Mitigation: merge — server snapshot
 *      wins for shared ids it knows about UNLESS the id is flagged in
 *      EITHER set, in which case local reactions are preserved over the
 *      older snapshot; local-only ids are appended at the tail.
 *   4. Same-hash guard — skip `applyMessages` (which calls
 *      `setState + renderMessages`) when the merged result hashes to the
 *      current `state.chatMessages`. Prevents pointless DOM rebuild +
 *      scroll jump on every flip.
 *
 * `applyMessages` is a closure passed in from `renderChatView` so the
 * reconcile fn can reuse the view's `renderMessages` without depending
 * on internal closure shape. Exported for test access.
 */
export async function reconcileChatFromServer(
  applyMessages: (messages: ChatMessage[]) => void,
): Promise<{ changed: boolean; skipped: 'inflight' | 'route' | 'same-hash' | 'no-state' | 'error' | null }> {
  // Note on the 15s gate timeout for chat: `/api/.../chat` hits Workbox's 10s
  // catch-all rule (NOT the 5s `/player-state` NetworkFirst rule), so the page-
  // side abort still fires AFTER Workbox would have completed. The 'error'
  // sentinel is returned on AbortError or any throw — caller is fire-and-forget,
  // so the next visibility/pageshow tick retries and the live WS catches up
  // any new messages once the TLS pool unwedges.
  return chatGate.run<{
    changed: boolean;
    skipped: 'inflight' | 'route' | 'same-hash' | 'no-state' | 'error' | null;
  }>(
    { changed: false, skipped: 'inflight' },
    { changed: false, skipped: 'error' },
    async (signal) => {
      const state = getState();
      if (!state.gameCode || !state.playerId) return { changed: false, skipped: 'no-state' };
      // Open a fresh in-flight tracking window. WS REACTION_CHANGED handler
      // populates this set while the lock is held. See comment on declaration.
      chatReactionInflightUpdates.clear();
      const messages = await getChatMessages(state.gameCode, state.playerId, signal);
      // Route guard. The fetch is async; user may have tapped Назад / kebab
      // mid-flight, swapping the app's view. Without this check, a stale
      // resolver would call applyMessages → renderMessages, which references
      // the now-detached `#chat-messages` element via closure.
      if (typeof window !== 'undefined' && !window.location.hash.startsWith('#/chat')) {
        return { changed: false, skipped: 'route' };
      }
      // WS-merge — see Guard 3 above. Server snapshot generally wins for shared
      // ids (suspension recovery: server has reactions added while WS was dead).
      // EXCEPTIONS — preserve LOCAL reactions when:
      //   - id is in `chatReactionInflightUpdates`: REACTION_CHANGED arrived
      //     between fetch start and resolve, so local has the live WS push.
      //   - id is in `chatReactionPendingToggles`: a local toggle POST is in
      //     flight (or already failed and reverted) — server snapshot may
      //     either lag the optimistic state or post-date a failed revert.
      const current = getState().chatMessages;
      const localById = new Map(current.map(m => [m.id, m]));
      const idsInServer = new Set(messages.map(m => m.id));
      const merged = messages.map(srv => {
        if (chatReactionInflightUpdates.has(srv.id) || chatReactionPendingToggles.has(srv.id)) {
          const loc = localById.get(srv.id);
          if (loc) return { ...srv, reactions: loc.reactions };
        }
        return srv;
      });
      const localOnly = current.filter(m => !idsInServer.has(m.id));
      const finalMerged = localOnly.length === 0 ? merged : [...merged, ...localOnly];
      // Same-hash guard against current local state. Compare merged-vs-current
      // (not raw-server-vs-current); otherwise a stale resolver after a WS push
      // would always trip the not-equal branch and re-render even though the
      // merge is a no-op against current state.
      const newHash = chatMessagesHash(finalMerged);
      const currentHash = chatMessagesHash(current);
      if (newHash === currentHash) {
        return { changed: false, skipped: 'same-hash' };
      }
      applyMessages(finalMerged);
      return { changed: true, skipped: null };
    },
  );
}

export async function renderChatView(container: HTMLElement): Promise<void> {
  const state = getState();

  container.innerHTML = `
    <div class="screen screen-enter" style="padding-bottom: 0;">
      <div style="display: flex; align-items: center; gap: var(--sp-md); margin-bottom: var(--sp-md);">
        <button id="chat-back" class="btn btn-outline" style="min-height: var(--touch-md); padding: var(--sp-sm) var(--sp-md); font-size: 14px;">Назад</button>
        <div style="flex: 1;">
          <p class="text-eyebrow">${state.allowTeamChat ? 'ЧАТ' : 'ЧАТ С ОРГАНИЗАТОРОМ'}</p>
          <p class="text-label" style="color: var(--text-secondary);">${escapeHtml(state.playerName)}</p>
        </div>
        <div id="chat-menu"></div>
      </div>

      <div id="chat-messages" style="flex: 1; overflow-y: auto; margin: 0 calc(-1 * var(--screen-padding)); padding: 0 var(--screen-padding);">
        <div id="chat-empty" class="text-body" style="color: var(--text-hint); text-align: center; padding: var(--sp-xl);">
          Пока нет сообщений
        </div>
      </div>

      <div id="chat-emoji-panel" style="position:relative;"></div>
      <div style="display: flex; gap: var(--sp-sm); padding: var(--sp-md) 0; padding-bottom: max(var(--sp-md), env(safe-area-inset-bottom)); align-items: flex-end;">
        ${state.isCaptain || state.allowTeamChat ? `
          <button id="chat-emoji-btn" class="btn btn-outline" style="min-width:44px;min-height:44px;padding:0;font-size:20px;" aria-label="Эмодзи">😊</button>
          <button id="chat-photo-btn" class="btn btn-outline" style="min-width:44px;min-height:44px;padding:0;font-size:18px;" aria-label="Прикрепить фото">📷</button>
          <!-- Two file inputs — one for camera (capture=environment forces back camera),
               one for gallery (no capture attr → OS file picker). User picks via the
               action sheet that the photo button opens. Pre-fix the single input had
               capture=environment hardcoded, which on iOS skipped the gallery option
               entirely (capture forces the camera UI). -->
          <input id="chat-file-input-camera" type="file" accept="image/*" capture="environment" style="display:none;" />
          <input id="chat-file-input-gallery" type="file" accept="image/*" style="display:none;" />
          <input id="chat-input" class="input" type="text" placeholder="Сообщение..." style="flex: 1;" />
          <button id="chat-send" class="btn btn-primary" style="min-height: 44px; padding: 0 var(--sp-lg);">
            ➤
          </button>
        ` : `
          <p class="text-body" style="color: var(--text-hint); text-align: center; flex: 1; padding: var(--sp-md);">
            Только капитан может отправлять сообщения
          </p>
        `}
      </div>
      <div id="chat-lightbox" style="display:none;position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,0.9);justify-content:center;align-items:center;cursor:pointer;">
        <img id="chat-lightbox-img" style="max-width:90vw;max-height:90vh;object-fit:contain;" />
        <video id="chat-lightbox-video" controls playsinline style="display:none;max-width:90vw;max-height:90vh;" ></video>
      </div>
    </div>
  `;

  const messagesEl = container.querySelector<HTMLElement>('#chat-messages')!;
  const emptyEl = container.querySelector<HTMLElement>('#chat-empty')!;

  // /investigate 2026-04-30 iOS Safari WS-suspend — closure used by both
  // cold-mount and the visibility/pageshow handlers below. Single source
  // of truth for «server says these are the messages, paint them». Any
  // future tweak (e.g. a smarter scroll restore) lives here, not in three
  // copies. Reads from `state` captured at function entry — that's fine
  // because the apply path only depends on `gameCode` (immutable for the
  // life of this view) and on the message array passed in.
  const applyMessages = (messages: ChatMessage[]): void => {
    setState({ chatMessages: messages, unreadChatCount: 0 });
    // Session 62: sync chat-poll baseline with what the player just saw,
    // otherwise the next background poll would treat these same messages
    // as "new" and resurrect the badge the moment the player leaves chat.
    acknowledgeChatThrough(messages);
    renderMessages(messages);
  };

  // Load existing messages
  try {
    const messages = await getChatMessages(state.gameCode, state.playerId);
    applyMessages(messages);
  } catch { /* ignore, will get via WS */ }

  // Plan B Task 7 (2026-05-11) — visibility/pageshow + watchdog + lightbox
  // Escape lifecycle is delegated to `activeLc`. The composite callback to
  // `installVisibilityReconcile` does BOTH the reconcile AND the Bug 2
  // forceReconnect (Option A — preserved verbatim from the pre-extraction
  // shape). iOS Safari fires visibilitychange + pageshow within milliseconds
  // on resume; both events route through the SAME callback so the recovery
  // body is identical for both branches. The pageshow.persisted filter and
  // the visibilityState === 'visible' filter live INSIDE the installer —
  // call sites can't get them wrong.
  //
  // Without `ws?.forceReconnect()`, iOS Safari leaves the chat WS in
  // stuck-CONNECTING after bg→fg, banner «Переподключение…» persists
  // forever, and live frames (org messages, peer messages) silently never
  // arrive after resume. The reconcile is HTTP-only; it doesn't touch
  // the socket without the explicit kick.
  activeLc = createViewLifecycle();

  installVisibilityReconcile(activeLc, () => {
    void reconcileChatFromServer(applyMessages);
    ws?.forceReconnect();
  }, '#/chat');

  function renderMessages(messages: ChatMessage[]): void {
    if (messages.length === 0) {
      emptyEl.style.display = 'block';
      return;
    }
    emptyEl.style.display = 'none';

    const seen = new Set<string>();
    const unique = messages.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; });

    messagesEl.innerHTML = unique.map(m => {
      const isOrganizer = m.senderType === 'ORGANIZER';
      const time = new Date(m.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      // Session 62: organizer header label is a constant, not m.teamName.
      // m.teamName on org rows stores the TARGET team, so the prior code
      // showed the team's own name at the top of every incoming org message,
      // which looked like "сообщение от нас самих".
      const headerLabel = isOrganizer ? 'Организатор' : m.teamName;
      // Session 62: explicit text color so organizer bubbles are legible in
      // both themes. Without it the message <p> inherited from distant
      // ancestors and rendered invisibly in dark (#000 on #1C1C2E) per user
      // report. Light theme also had too-low contrast (bubble blended into
      // screen bg); 1px border separates the bubble even on a light surface.
      const bubbleBorder = isOrganizer
        ? 'border: 1px solid var(--bg-border);'
        : 'border: 1px solid rgba(255, 184, 0, 0.35);';
      // Session 84.3 — reactions UX redesigned. Default render shows ONLY
      // active reactions (count > 0) as small pills under the bubble, plus
      // a quiet «+» affordance to open the picker. Tapping the bubble or
      // the «+» opens a popover with all 5 presets; tapping a preset
      // toggles + closes the popover. Pre-fix the row showed all 5 presets
      // permanently, which the user reported as a permanent emoji strip
      // under every message instead of opt-in reactions.
      const reactions: ReactionTally[] = m.reactions ?? [];
      const activeReactions = reactions.filter(r => r.count > 0);
      const reactionPills = activeReactions.map(r => {
        const bg = r.mine
          ? 'background: rgba(255, 184, 0, 0.25); border-color: var(--amber);'
          : 'background: transparent; border-color: var(--bg-border);';
        return `<button
          type="button"
          class="chat-reaction-pill${r.mine ? ' chat-reaction-pill--mine' : ''}"
          data-msg-id="${escapeHtml(m.id)}"
          data-emoji="${escapeHtml(r.emoji)}"
          aria-pressed="${r.mine}"
          aria-label="${r.emoji} (${r.count})"
          style="min-height: 28px; padding: 2px 8px; border: 1px solid; ${bg}
                 border-radius: 14px; color: var(--text-primary);
                 font-size: 13px; line-height: 1; cursor: pointer;
                 display: inline-flex; align-items: center; gap: 4px;">
          <span aria-hidden="true">${r.emoji}</span><span style="font-family: var(--font-mono); font-size: 11px;">${r.count}</span>
        </button>`;
      }).join('');
      // «+» trigger only on bubbles where the user might want to add a
      // reaction. Always rendered — server-side gates (lobby messages,
      // fullOffline, missing token) are enforced on POST. Local PWA
      // hides the row entirely if `state.allowTeamChat=false`? No — that
      // controls the SEND input, not viewing/reacting. Reactions stay
      // available even in captain-only chat.
      const addPickerBtn = `<button
        type="button"
        class="chat-reaction-add"
        data-msg-id="${escapeHtml(m.id)}"
        aria-label="Добавить реакцию"
        style="min-height: 28px; min-width: 32px; padding: 2px 8px;
               border: 1px dashed var(--bg-border); background: transparent;
               border-radius: 14px; color: var(--text-hint);
               font-size: 16px; line-height: 1; cursor: pointer;">+</button>`;
      return `
        <div style="display: flex; flex-direction: column; align-items: ${isOrganizer ? 'flex-start' : 'flex-end'}; margin-bottom: var(--sp-sm); gap: 4px;">
          <div style="max-width: 80%; padding: var(--sp-sm) var(--sp-md);
                      background: ${isOrganizer ? 'var(--bg-card)' : 'rgba(255, 184, 0, 0.15)'};
                      color: var(--text-primary);
                      ${bubbleBorder}
                      border-radius: var(--radius-lg);
                      ${isOrganizer ? 'border-top-left-radius: var(--radius-sm);' : 'border-top-right-radius: var(--radius-sm);'}">
            <p class="text-label" style="color: ${isOrganizer ? 'var(--amber)' : 'var(--success)'}; font-size: 12px; margin-bottom: 2px;">
              ${escapeHtml(headerLabel)}
            </p>
            ${m.mediaUrl ? (/\.(mp4|webm)$/i.test(m.mediaUrl)
              ? `<video src="${escapeHtml(m.mediaUrl)}" class="chat-media-video" controls playsinline preload="metadata" style="max-width:min(240px,70%);border-radius:var(--radius-md);display:block;margin-bottom:4px;"></video>`
              : `<img src="${escapeHtml(m.mediaUrl)}" class="chat-media" alt="Фото" loading="lazy" style="max-width:min(200px,60%);border-radius:var(--radius-md);cursor:pointer;display:block;margin-bottom:4px;" data-fullsrc="${escapeHtml(m.mediaUrl)}" />`)
            : ''}
            ${m.message ? `<p class="text-body" style="word-break: break-word; color: var(--text-primary);">${escapeHtml(m.message)}</p>` : ''}
            <p style="font-size: 12px; color: var(--text-hint); text-align: right; margin-top: 2px;">${time}</p>
          </div>
          <div class="chat-reaction-row" data-msg-id="${escapeHtml(m.id)}" style="display: flex; gap: 4px; flex-wrap: wrap; max-width: 80%; align-items: center;">
            ${reactionPills}${addPickerBtn}
          </div>
        </div>
      `;
    }).join('');

    // Session 84.3 — single delegated click listener handles three things:
    //   1. Tap on existing pill → toggle that emoji.
    //   2. Tap on «+» add-button → open picker for that message.
    //   3. Tap inside an open picker on a preset → toggle emoji + close.
    // Re-render replaces the listener target each time, so the listener
    // also sits on the parent `messagesEl` for outside-click detection.
    messagesEl.querySelectorAll<HTMLButtonElement>('.chat-reaction-pill').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const msgId = btn.dataset.msgId;
        const emoji = btn.dataset.emoji;
        if (!msgId || !emoji) return;
        handleToggleReaction(msgId, emoji);
      });
    });
    messagesEl.querySelectorAll<HTMLButtonElement>('.chat-reaction-add').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const msgId = btn.dataset.msgId;
        if (!msgId) return;
        openReactionPicker(msgId, btn);
      });
    });

    messagesEl.scrollTop = messagesEl.scrollHeight;

    // Wire media click → lightbox
    messagesEl.querySelectorAll<HTMLImageElement>('.chat-media').forEach(img => {
      img.addEventListener('click', () => {
        const lightbox = container.querySelector<HTMLElement>('#chat-lightbox');
        const lightboxImg = container.querySelector<HTMLImageElement>('#chat-lightbox-img');
        if (lightbox && lightboxImg) {
          lightboxImg.src = img.dataset.fullsrc || img.src;
          lightbox.style.display = 'flex';
        }
      });
    });
  }

  // WebSocket
  ws = new ReconnectingWebSocket(
    wsUrl(`/ws/games/${state.gameCode}/chat?playerId=${state.playerId}`),
    (connected) => {
      setState({ wsConnected: connected });
      if (connected) hideStatus();
      else showReconnecting();
    }
  );

  ws.on('CHAT_MESSAGE', (msg: unknown) => {
    const envelope = msg as { type: string; message?: ChatMessage };
    const data = envelope.message;
    if (!data) return;
    const messages = [...getState().chatMessages];
    if (!messages.find(m => m.id === data.id)) {
      messages.push(data);
      setState({ chatMessages: messages });
      // Session 62: roll the chat-poll baseline forward so the next poll
      // after the player leaves /chat doesn't re-flag this as unread.
      acknowledgeChatThrough(messages);
      renderMessages(messages);
      // Browser notification when tab is hidden
      const sender = data.senderType === 'ORGANIZER' ? 'Организатор' : data.teamName;
      const body = data.mediaUrl ? 'Отправил фото' : data.message;
      showChatNotification(sender, body);
    }
  });

  // Session 70 (#13) — reaction-changed WS event. Merge logic lives in
  // `utils/reaction-merge.ts` (pure, behaviorally tested).
  ws.on('REACTION_CHANGED', (msg: unknown) => {
    const evt = msg as ReactionChangedEvent;
    if (!evt.messageId || !evt.reactions || !evt.emoji) return;
    const myId = getState().playerId;
    const messages = applyReactionChangedEvent(getState().chatMessages, evt, myId);
    if (messages === getState().chatMessages) return; // no-op (message id not in history)
    setState({ chatMessages: messages });
    // /review 2026-04-30 codex finding — flag this id so an in-flight
    // reconcile prefers local reactions over its older server snapshot.
    if (chatGate.isInflight) {
      chatReactionInflightUpdates.add(evt.messageId);
    }
    renderMessages(messages);
  });

  // Session 84.3 — body-attached reaction picker. Opens on «+» tap;
  // closes on preset-tap, outside-click, or Escape. Mirror of the dashboard
  // picker (DashboardGameDetailPage.kt) so both surfaces share the same
  // tap-to-react UX. Single live picker at a time — opening for a new
  // message destroys the prior one.
  let activePicker: { panel: HTMLElement; outsideHandler: (e: Event) => void; escHandler: (e: KeyboardEvent) => void } | null = null;
  function closeReactionPicker(): void {
    if (!activePicker) return;
    document.removeEventListener('click', activePicker.outsideHandler, true);
    document.removeEventListener('keydown', activePicker.escHandler);
    activePicker.panel.remove();
    activePicker = null;
    closeActiveReactionPicker = null;
  }
  function openReactionPicker(messageId: string, anchor: HTMLElement): void {
    closeReactionPicker();
    const panel = document.createElement('div');
    panel.className = 'chat-reaction-picker';
    panel.setAttribute('role', 'menu');
    panel.style.cssText = `
      position: fixed; z-index: 200;
      background: var(--bg-card); border: 1px solid var(--bg-border);
      border-radius: 18px; padding: 6px; display: flex; gap: 4px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.35);
    `;
    REACTION_PRESETS.forEach(emoji => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'chat-reaction-picker-btn';
      btn.dataset.emoji = emoji;
      btn.setAttribute('aria-label', `Реакция ${emoji}`);
      btn.style.cssText = `
        background: transparent; border: none; cursor: pointer;
        font-size: 22px; padding: 4px 8px; border-radius: 12px;
        line-height: 1;
      `;
      btn.textContent = emoji;
      btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(255,184,0,0.15)'; });
      btn.addEventListener('mouseleave', () => { btn.style.background = 'transparent'; });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        handleToggleReaction(messageId, emoji);
        closeReactionPicker();
      });
      panel.appendChild(btn);
    });
    document.body.appendChild(panel);
    // Position above the anchor; flip to below if no room.
    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    let top = anchorRect.top - panelRect.height - 8;
    if (top < 8) top = anchorRect.bottom + 8;
    let left = anchorRect.left + (anchorRect.width / 2) - (panelRect.width / 2);
    left = Math.max(8, Math.min(left, window.innerWidth - panelRect.width - 8));
    panel.style.top = `${top}px`;
    panel.style.left = `${left}px`;
    const outsideHandler = (e: Event) => {
      if (!panel.contains(e.target as Node)) closeReactionPicker();
    };
    const escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeReactionPicker(); };
    // capture=true so outside taps are detected before the click bubbles
    // up to the original «+» button (which would re-open us).
    setTimeout(() => document.addEventListener('click', outsideHandler, true), 0);
    document.addEventListener('keydown', escHandler);
    activePicker = { panel, outsideHandler, escHandler };
    closeActiveReactionPicker = closeReactionPicker;
  }

  async function handleToggleReaction(messageId: string, emoji: string): Promise<void> {
    const s = getState();
    if (!s.gameCode || !s.playerId || !s.playerToken) return;
    // /review 2026-04-30 codex round 7 (Medium) — open or extend the
    // batch for this message. Origin is captured at FIRST toggle in the
    // batch (before any optimistic mutation) so an all-fail outcome can
    // walk cumulative state back to pre-batch. See `chatReactionBatches`
    // declaration for the repro and full rationale.
    let batch = chatReactionBatches.get(messageId);
    if (!batch) {
      const msg = s.chatMessages.find(m => m.id === messageId);
      batch = {
        origin: [...(msg?.reactions ?? [])],
        hasSuccess: false,
        pendingCount: 0,
      };
      chatReactionBatches.set(messageId, batch);
    }
    batch.pendingCount += 1;
    // /review 2026-04-30 codex round 5 (Medium) — capture generation BEFORE
    // optimistic mutation. Two overlapping toggles on the same messageId
    // each get a distinct gen; only the LATEST gen owns mutating state on
    // resolve. Earlier-issued (now-stale) resolves drop into the gen-mismatch
    // bail and just decrement the pending marker. See the long comment on
    // `chatReactionToggleGeneration` declaration for the repro.
    const myGen = (chatReactionToggleGeneration.get(messageId) ?? 0) + 1;
    chatReactionToggleGeneration.set(messageId, myGen);
    // Optimistic update — patch local state before the network round-trip
    // so the UI feels instant. Pure merge logic lives in reaction-merge.ts.
    const optimistic = computeOptimisticToggle(s.chatMessages, messageId, emoji);
    setState({ chatMessages: optimistic });
    // /review 2026-04-30 codex follow-up — track the local POST roundtrip
    // in `chatReactionPendingToggles` (NOT `chatReactionInflightUpdates`).
    // The WS-handler set is cleared at every reconcile fetch start; if a
    // reconcile begins AFTER this optimistic mutation but BEFORE the POST
    // resolves, the WS-set flag would be wiped and the merge would apply
    // the older server snapshot, producing a visible revert/flicker until
    // the POST self-heals. The pending-toggles set is never cleared at
    // reconcile boundaries — only when this POST resolves (success or
    // failure) — so it survives any reconcile that fires within the POST
    // roundtrip.
    chatReactionPendingToggles.add(messageId);
    renderMessages(optimistic);

    // Helper — drains batch refcount + per-id pending tracker on every
    // resolve path. Deletes batch entry when this is the last in flight.
    const finalize = (): void => {
      batch!.pendingCount -= 1;
      if (batch!.pendingCount === 0) {
        chatReactionBatches.delete(messageId);
      }
      chatReactionPendingToggles.delete(messageId);
    };

    try {
      const result: ReactionToggleResultData = await toggleChatReaction(
        s.gameCode, messageId, s.playerId, s.playerToken, emoji
      );
      // Mark batch as having seen a success EVEN IF this resolve is
      // gen-stale and won't apply tally. The flag is consulted by tail
      // failures to decide between "restore origin" (no success) vs
      // "invert optimistic" (success was seen).
      batch.hasSuccess = true;
      // Stale-resolve guard. A newer toggle on this messageId was issued
      // AFTER us; the latest toggle's resolve owns reconciling state.
      // Skipping is safe because (a) the latest toggle's resolve will
      // apply server's tally against current state, and (b) the WS
      // REACTION_CHANGED broadcast for THIS toggle's server-side commit
      // still arrives independently and patches state via
      // applyReactionChangedEvent. Just decrement the marker so refcount
      // stays accurate.
      if (chatReactionToggleGeneration.get(messageId) !== myGen) {
        finalize();
        return;
      }
      const reconciled = reconcileReactionToggle(getState().chatMessages, result);
      setState({ chatMessages: reconciled });
      // POST confirmed — local state now matches server's authoritative
      // tally (server's reaction-changed event will broadcast the same
      // result). Drop the pending marker so future reconciles can take
      // fresh server snapshots without «local wins» suppression.
      finalize();
      renderMessages(reconciled);
    } catch (err) {
      console.warn('[chat] toggle reaction failed', err);
      batch.pendingCount -= 1;
      const isLastInBatch = batch.pendingCount === 0;
      const isStale = chatReactionToggleGeneration.get(messageId) !== myGen;

      // /review 2026-04-30 codex round 7 (Medium) — last-to-settle in a
      // batch with NO successful resolves means the server never
      // committed any toggle in this batch. The cumulative optimistic
      // state on screen is wrong by exactly the number of toggles that
      // ran. Restore the message's reactions to the snapshot taken at
      // batch start. This branch fires regardless of gen-staleness:
      // when both T1 and T2 fail, T2 inverts mid-batch (latest gen,
      // not last) and T1 (stale gen, last to settle) restores origin.
      if (isLastInBatch && !batch.hasSuccess) {
        const originReactions = batch.origin;
        chatReactionBatches.delete(messageId);
        chatReactionPendingToggles.delete(messageId);
        const restored = getState().chatMessages.map(m =>
          m.id === messageId ? { ...m, reactions: [...originReactions] } : m
        );
        setState({ chatMessages: restored });
        renderMessages(restored);
        return;
      }

      if (isStale) {
        // Newer toggle owns mutation, OR an earlier success has already
        // anchored state. Just drain bookkeeping without touching state.
        if (isLastInBatch) chatReactionBatches.delete(messageId);
        chatReactionPendingToggles.delete(messageId);
        return;
      }

      // /review 2026-04-30 codex round 6 (Medium) — invert the optimistic
      // mutation on CURRENT state, not on a stale pre-tap whole-array
      // snapshot. The pre-fix code restored the entire chat list as it
      // was BEFORE this toggle started, which silently wiped any unrelated
      // CHAT_MESSAGE / REACTION_CHANGED that landed during the POST flight
      // (new messages disappear, teammate reactions on other messages
      // revert). Tap-toggle is its own inverse — calling
      // computeOptimisticToggle a second time on the same emoji reverses
      // count/mine. Only the target message's reactions change; structural
      // array updates and other-message state survive.
      const reverted = computeOptimisticToggle(getState().chatMessages, messageId, emoji);
      setState({ chatMessages: reverted });
      // POST failed — local now reflects the pre-tap reaction state for
      // this messageId. Drop the pending marker so the next reconcile uses
      // server's authoritative tally for this id. Without this delete, a
      // teammate's newer reaction added while WS was down would be silently
      // suppressed by «local wins» until some later reconcile/event repaired it.
      if (isLastInBatch) chatReactionBatches.delete(messageId);
      chatReactionPendingToggles.delete(messageId);
      renderMessages(reverted);
    }
  }

  ws.connect();

  chatMenuHandle = createGameMenu(container.querySelector('#chat-menu')!, activeLc);

  // Timeout watchdog: while the player is in chat, keep checking if the
  // location timer has expired. Without this, triggerAdvance('TIMEOUT')
  // never fires because location-view's timer is destroyed on navigation.
  //
  // Plan B Task 7 (2026-05-11) — interval registered via activeLc.setInterval
  // so destroy releases it. ViewLifecycle has no per-handle cancel API by
  // design, so the one-shot semantics are preserved via a closure-local
  // `fired` flag: once triggerAdvance fires, every subsequent tick no-ops
  // until activeLc.cleanup() actually clears the interval. Pin via the
  // chat-timeout «fires exactly once» behavioural test.
  const loc = state.locations[state.currentLocationIndex];
  if (loc && state.locationStartedAt) {
    let watchdogFired = false;
    activeLc.setInterval(() => {
      if (watchdogFired) return;
      const s = getState();
      if (!s.locationStartedAt) return;
      const elapsed = getElapsedSec(s.locationStartedAt);
      if (elapsed >= loc.timeLimitSec) {
        watchdogFired = true;
        triggerAdvance('TIMEOUT', activeLc!);
      }
    }, 1000);
  }

  // Back
  container.querySelector('#chat-back')!.addEventListener('click', () => {
    setState({ unreadChatCount: 0 });
    navigate('riddle');
  });

  // Send
  const sendBtn = container.querySelector<HTMLButtonElement>('#chat-send');
  const chatInput = container.querySelector<HTMLInputElement>('#chat-input');

  if (sendBtn && chatInput) {
    const doSend = async () => {
      const text = chatInput.value.trim();
      if (!text) return;
      chatInput.value = '';
      try {
        await sendChatMessage(state.gameCode, state.playerId, text);
      } catch { /* will arrive via WS */ }
    };

    sendBtn.addEventListener('click', doSend);
    chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doSend();
    });

    // Emoji picker — single click opens grid directly
    const emojiBtn = container.querySelector<HTMLButtonElement>('#chat-emoji-btn');
    const emojiPanel = container.querySelector<HTMLElement>('#chat-emoji-panel');
    if (emojiBtn && emojiPanel) {
      const picker = createEmojiPicker(emojiPanel, (emoji: string) => {
        chatInput.value += emoji;
        chatInput.focus();
      });
      emojiBtn.addEventListener('click', (e) => { e.stopPropagation(); picker.toggle(); });
    }

    // Photo upload — user picks source (camera or gallery) via a small
    // action sheet anchored on the photo button. Pre-fix the single input
    // had capture="environment" hardcoded, so on iOS the OS skipped the
    // gallery picker entirely and dropped the user straight into the
    // camera UI. User report 2026-04-27: «Кнопка фото в чате игрока должна
    // открывать на выбор либо камеру либо галерею».
    const photoBtn = container.querySelector<HTMLButtonElement>('#chat-photo-btn');
    const fileInputCamera = container.querySelector<HTMLInputElement>('#chat-file-input-camera');
    const fileInputGallery = container.querySelector<HTMLInputElement>('#chat-file-input-gallery');
    if (photoBtn && fileInputCamera && fileInputGallery) {
      // Plan B Task 11 (2026-05-11) — picker extracted into
      // components/photo-source-picker.ts, registered through bodyOverlays
      // under the active view's lifecycle so destroyChatView's
      // activeLc.cleanup() sweeps it automatically.
      photoBtn.addEventListener('click', () => {
        openPhotoSourcePicker(photoBtn, activeLc!, (source) => {
          if (source === 'camera') fileInputCamera.click();
          else fileInputGallery.click();
        });
      });

      // Shared upload handler — fires on either input's change event.
      const handleSelectedFile = async (input: HTMLInputElement): Promise<void> => {
        const file = input.files?.[0];
        if (!file) return;
        input.value = ''; // reset for re-select (same file twice in a row)
        photoBtn.disabled = true;
        photoBtn.textContent = '⏳';
        try {
          const { uploadChatMedia } = await import('../api/game-api');
          const result = await uploadChatMedia(file);
          await sendChatMessage(state.gameCode, state.playerId, '', false, result.url);
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : 'Ошибка загрузки';
          alert(errMsg);
        } finally {
          photoBtn.disabled = false;
          photoBtn.textContent = '📷';
        }
      };
      fileInputCamera.addEventListener('change', () => handleSelectedFile(fileInputCamera));
      fileInputGallery.addEventListener('change', () => handleSelectedFile(fileInputGallery));
    }
  }

  // Lightbox for media
  //
  // Plan B Task 7 (2026-05-11) — Escape handler registered via
  // activeLc.addListener so destroy removes it. The pre-extraction
  // `let lightboxKeyHandler` module-level slot is gone; the const is
  // closure-local to renderChatView and the lc owns its lifecycle.
  const lightbox = container.querySelector<HTMLElement>('#chat-lightbox');
  const lightboxImg = container.querySelector<HTMLImageElement>('#chat-lightbox-img');
  if (lightbox && lightboxImg) {
    lightbox.addEventListener('click', () => { lightbox.style.display = 'none'; });
    const lightboxKeyHandler = (e: Event): void => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Escape' && lightbox.style.display !== 'none') {
        lightbox.style.display = 'none';
      }
    };
    activeLc.addListener(document, 'keydown', lightboxKeyHandler);
  }

  // Plan B Task 7 (2026-05-11) — module-scoped handles cleaned via lc.onDestroy
  // so cleanup reads the LATEST reference at teardown time. createGameMenu's
  // return reference is captured into chatMenuHandle once (above); reaction
  // picker's close fn is stashed into closeActiveReactionPicker by every
  // openReactionPicker() call (mutates mid-render). Both are nulled after
  // teardown to keep destroy idempotent and break the cycle.
  activeLc.onDestroy(() => {
    chatMenuHandle?.destroy();
    chatMenuHandle = null;
    closeActiveReactionPicker?.();
    closeActiveReactionPicker = null;
  });
}

export function destroyChatView(): void {
  // Plan B Task 7 (2026-05-11) — timeout watchdog + lightbox key handler
  // + visibility/pageshow listeners + chatMenuHandle.destroy +
  // closeActiveReactionPicker are released via activeLc.cleanup(). The
  // genuinely module-scoped bits (ws, chatGate, photo-source picker,
  // reaction-tracking maps, connection-bar, auto-advance overlay) stay in
  // the explicit destroy below because their lifecycles cross renders or
  // they outlive the view (overlay, connection bar) and the photo-source
  // picker migrates in Task 11.
  activeLc?.cleanup();
  activeLc = null;

  if (ws) { ws.close(); ws = null; }
  // /review 2026-04-30 second-pass — drop the inflight lock if it's still
  // held. Without this, a fetch that was awaiting on destroy keeps the
  // module-level lock until its `finally` runs; the NEXT mount's first
  // visibility/pageshow flip then bails with `'inflight'` and silently
  // skips the resume reconcile that this whole audit exists to deliver.
  // Safe because the in-flight fetch's resolve still runs `applyMessages`
  // against the stale closure (writing to the now-detached `messagesEl`),
  // but the merge uses `getState().chatMessages` so global state stays
  // consistent with the new mount's view.
  chatGate.release();
  // Plan B Task 11 (2026-05-11) — photo-source picker singleton now lives in
  // bodyOverlays. Its dismiss closure is registered via activeLc.onDestroy,
  // so the activeLc?.cleanup() call above sweeps it automatically. Pre-extraction
  // this was an explicit module-level slot sweep added in v0.80.9 (2026-05-04)
  // — same global-singleton class as the connection-bar.
  // Plan B Task 7 — reset reaction tracking maps so a fresh mount starts
  // with a clean slate. Production never re-enters chat without a destroy
  // first, but the maps span renders for the in-flight reaction protocol
  // and would otherwise carry stale ids from the prior session.
  chatReactionInflightUpdates.clear();
  chatReactionPendingToggles.clear();
  chatReactionToggleGeneration.clear();
  chatReactionBatches.clear();
  // Plan B Task 9 (2026-05-11) — auto-advance full-screen overlay now lives
  // in bodyOverlays. game-menu's «Пропустить этап» item is reachable from
  // chat-view's kebab too; the overlay's lc.onDestroy hook fires through
  // `activeLc?.cleanup()` above, so no explicit sweep is needed here.
  // Pre-extraction this was `clearAutoAdvanceOverlay()`.
  hideStatus();
}
